// ── Foreman Equipment Panel ─────────────────────────────────────────────────
// Allows foremans to view equipment at their allowed sites and transfer items.
//
// Project-to-site mapping is now loaded dynamically from the projects table
// (column projects.site_code), so adding a project in the admin UI immediately
// makes its site code available here — no code changes needed.

// Loaded lazily from the DB on first equipment-overlay open.
// _projectSiteMap: full project name (as stored in profiles.allowed_projects)
//                  → short site_code (e.g. "LASP", "CENTRA"). NULL site_codes
//                  are omitted so projects without equipment don't show up.
// _projectCodeMap: project code prefix ("26TM001") → site_code, used as a
//                  fallback when allowed_projects contains older or trimmed names.
// _eqAllSites:     union of all known site codes (projects.site_code ∪
//                  equipment_locations.site_name) — used when the foreman has
//                  no allowed_projects set (treated as "see everything").
let _projectSiteMap = new Map();
let _projectCodeMap = new Map();
let _eqAllSites     = [];
let _projectSiteMapLoaded = false;

async function loadProjectSiteMappings() {
  const [projRes, locRes] = await Promise.all([
    sbClient.from('projects').select('name, site_code').eq('status', 'active'),
    sbClient.from('equipment_locations').select('site_name'),
  ]);

  _projectSiteMap = new Map();
  _projectCodeMap = new Map();
  const siteSet = new Set();

  (projRes.data || []).forEach(p => {
    if (!p.site_code) return;
    _projectSiteMap.set(p.name, p.site_code);
    // First whitespace-separated token of the name is the project code (e.g. "26TM001")
    const code = (p.name || '').trim().split(/\s+/)[0].toUpperCase();
    if (code) _projectCodeMap.set(code, p.site_code);
    siteSet.add(p.site_code);
  });
  (locRes.data || []).forEach(l => { if (l.site_name) siteSet.add(l.site_name); });

  _eqAllSites = Array.from(siteSet).sort();
  _projectSiteMapLoaded = true;
}

// ── State ──────────────────────────────────────────────────────────────────────
let _fTransferItem  = null;
let _fEqCache       = [];   // flat array of { id, name, category, locs, total }
let _fEqSites       = [];   // allowed site abbreviations for this foreman
let _fEqCatFilter   = '';   // active category pill

// ── Open / close ───────────────────────────────────────────────────────────────
function openEquipmentOverlay() {
  showInnerPage('equipmentPage');
  // Reset search + pills on every open
  const searchEl = document.getElementById('fEqSearch');
  if (searchEl) searchEl.value = '';
  _fEqCatFilter = '';
  document.querySelectorAll('.feq-cat-pill').forEach((b, i) => {
    b.classList.toggle('active', i === 0);
  });
  loadForemanEquipment();
}

function closeEquipmentOverlay() { goHome(); }

// YARD is the company warehouse and is always available as a transfer
// destination (so foremans can return equipment when a job ends, even if
// YARD isn't one of their assigned project sites).
const YARD_ALWAYS_ALLOWED = 'YARD';

// Returns the foreman's allowed destinations: their assigned sites + YARD.
function _foremanAllowedDestinations() {
  const set = new Set(_fEqSites);
  set.add(YARD_ALWAYS_ALLOWED);
  return Array.from(set);
}

// Populates the foreman's transfer "To" dropdown (#fTransferTo) — restricted
// to the foreman's assigned sites plus YARD. Called from loadForemanEquipment()
// AFTER _fEqSites has been calculated.
function populateForemanTransferTo() {
  const sel = document.getElementById('fTransferTo');
  if (!sel) return;
  sel.innerHTML = '<option value="">Select destination…</option>' +
    _foremanAllowedDestinations()
      .map(s => `<option value="${escEq(s)}">${escEq(s)}</option>`)
      .join('');
}

// ── Load from DB (once per open) ───────────────────────────────────────────────
async function loadForemanEquipment() {
  const listEl = document.getElementById('fEqList');
  if (!listEl) return;
  listEl.innerHTML = '<div class="feq-loading">Loading…</div>';

  // Ensure the project → site_code mapping is loaded (cached after first call).
  if (!_projectSiteMapLoaded) await loadProjectSiteMappings();

  // Determine visible sites from foreman's allowed projects.
  // Tries full-name match first (most reliable), falls back to project-code
  // prefix lookup (e.g. "26TM001" extracted from a slightly different name).
  const allowedProjects = window.currentProfile?.allowed_projects || [];
  const visibleSites = allowedProjects.length > 0
    ? [...new Set(allowedProjects.map(p => {
        const name = (p || '').trim();
        const byName = _projectSiteMap.get(name);
        if (byName) return byName;
        const code = name.split(/\s+/)[0].toUpperCase();
        return _projectCodeMap.get(code) || null;
      }).filter(Boolean))]
    : _eqAllSites;

  _fEqSites = visibleSites.length > 0 ? visibleSites : _eqAllSites;

  // Populate the foreman transfer "To" dropdown — must run AFTER _fEqSites is
  // set, because we restrict the destination list to the foreman's own sites.
  populateForemanTransferTo();

  const { data: items, error } = await sbClient
    .from('equipment')
    .select('id,category,name,sort_order,equipment_locations(site_name,quantity)')
    .order('category')
    .order('sort_order');

  if (error || !items?.length) {
    listEl.innerHTML = '<div class="feq-empty">No equipment found.</div>';
    return;
  }

  // Build flat cache — only items with qty > 0 at at least one visible site
  _fEqCache = [];
  items.forEach(item => {
    const locs = {};
    (item.equipment_locations || []).forEach(l => { locs[l.site_name] = l.quantity; });
    const total = _fEqSites.reduce((sum, s) => sum + (locs[s] || 0), 0);
    if (total === 0) return;
    _fEqCache.push({ id: item.id, name: item.name, category: item.category, locs, total });
  });

  if (!_fEqCache.length) {
    listEl.innerHTML = '<div class="feq-empty">No equipment at your sites.</div>';
    return;
  }

  _renderEquipmentList();
}

// ── Category pill selection ────────────────────────────────────────────────────
function selectEqCat(btn, cat) {
  document.querySelectorAll('.feq-cat-pill').forEach(b => b.classList.remove('active'));
  btn.classList.add('active');
  _fEqCatFilter = cat;
  _renderEquipmentList();
}

// ── Search input ───────────────────────────────────────────────────────────────
function filterEquipment() {
  _renderEquipmentList();
}

// ── Render the filtered list ───────────────────────────────────────────────────
function _renderEquipmentList() {
  const listEl = document.getElementById('fEqList');
  if (!listEl) return;

  const q = (document.getElementById('fEqSearch')?.value || '').toLowerCase().trim();

  const filtered = _fEqCache.filter(item => {
    const catMatch = !_fEqCatFilter || item.category === _fEqCatFilter;
    const nameMatch = !q || item.name.toLowerCase().includes(q) || item.category.toLowerCase().includes(q);
    return catMatch && nameMatch;
  });

  if (!filtered.length) {
    listEl.innerHTML = '<div class="feq-empty">No equipment matches your search.</div>';
    return;
  }

  // Group by category for section headers
  const grouped = {};
  filtered.forEach(item => {
    if (!grouped[item.category]) grouped[item.category] = [];
    grouped[item.category].push(item);
  });

  const CAT_ICONS = {
    'Vehicles':     '🚛',
    'Mixers':       '🌀',
    'Saws':         '🪚',
    'Power Tools':  '⚡',
    'Scaffold':     '🪜',
    'Miscellaneous':'📦',
  };

  listEl.innerHTML = Object.entries(grouped).map(([cat, catItems]) => `
    <div class="feq-section">
      <div class="feq-section-header">
        <span class="feq-section-icon">${CAT_ICONS[cat] || '🔧'}</span>
        <span class="feq-section-title">${escEq(cat)}</span>
        <span class="feq-section-count">${catItems.length}</span>
      </div>
      <div class="feq-card-grid">
        ${catItems.map(item => _renderItemCard(item)).join('')}
      </div>
    </div>`).join('');
}

function _renderItemCard(item) {
  const sitesWithQty = _fEqSites.filter(s => (item.locs[s] || 0) > 0);
  const locChips = sitesWithQty.map(s =>
    `<span class="feq-loc-chip">
      <span class="feq-loc-qty">${item.locs[s]}</span>
      <span class="feq-loc-name">${escEq(s)}</span>
    </span>`
  ).join('');

  const totalUnits = sitesWithQty.reduce((sum, s) => sum + (item.locs[s] || 0), 0);
  const itemJson = escEq(JSON.stringify({ id: item.id, name: item.name, category: item.category, locs: item.locs }));
  const sitesJson = escEq(JSON.stringify(_fEqSites));

  return `
    <div class="feq-card">
      <div class="feq-card-top">
        <div class="feq-card-name">${escEq(item.name)}</div>
        <div class="feq-card-total">${totalUnits} unit${totalUnits !== 1 ? 's' : ''}</div>
      </div>
      <div class="feq-card-locs">${locChips}</div>
      <button class="feq-card-transfer-btn"
        onclick='openFTransfer(${itemJson}, ${sitesJson})'>
        ⇄ Transfer
      </button>
    </div>`;
}

// ── Transfer modal ─────────────────────────────────────────────────────────────
function openFTransfer(item, allowedSites) {
  _fTransferItem = item;
  document.getElementById('fTransferName').textContent = item.name + ' (' + item.category + ')';
  document.getElementById('fTransferError').textContent = '';
  document.getElementById('fTransferQty').value = '';
  document.getElementById('fTransferNotes').value = '';
  document.getElementById('fTransferTo').value = '';

  const fromSel = document.getElementById('fTransferFrom');
  fromSel.innerHTML = '<option value="">Select origin site…</option>' +
    allowedSites
      .filter(s => (item.locs[s] || 0) > 0)
      .map(s => `<option value="${escEq(s)}">${escEq(s)} — ${item.locs[s]} available</option>`)
      .join('');

  document.getElementById('fTransferModal').style.display = 'flex';
}

function closeFTransfer() {
  document.getElementById('fTransferModal').style.display = 'none';
}

async function confirmFTransfer() {
  const from  = document.getElementById('fTransferFrom').value;
  const to    = document.getElementById('fTransferTo').value;
  const qty   = parseFloat(document.getElementById('fTransferQty').value);
  const notes = document.getElementById('fTransferNotes').value.trim();
  const errEl = document.getElementById('fTransferError');
  const item  = _fTransferItem;

  if (!from)           { errEl.textContent = 'Select an origin site.'; return; }
  if (!to)             { errEl.textContent = 'Select a destination.'; return; }
  if (from === to)     { errEl.textContent = 'Origin and destination cannot be the same.'; return; }
  if (!qty || qty < 1) { errEl.textContent = 'Enter a valid quantity (minimum 1).'; return; }

  // Belt-and-suspenders: even if the dropdown was tampered with, refuse to
  // transfer between sites the foreman isn't assigned to (YARD is always allowed
  // as a destination — see _foremanAllowedDestinations()).
  const allowedDest = _foremanAllowedDestinations();
  if (!_fEqSites.includes(from))   { errEl.textContent = 'You can only transfer from your assigned sites.'; return; }
  if (!allowedDest.includes(to))   { errEl.textContent = 'You can only transfer to your assigned sites or YARD.'; return; }

  const available = item.locs[from] || 0;
  if (qty > available) { errEl.textContent = `Only ${available} available at ${from}.`; return; }

  errEl.textContent = '';

  const confirmBtn = document.querySelector('#fTransferModal .btn-next');
  if (confirmBtn) { confirmBtn.disabled = true; confirmBtn.textContent = 'Submitting…'; }

  try {
    // Foreman transfers go into a "pending" queue — equipment_locations is
    // NOT updated yet. The admin reviews the request and either approves
    // (moves the stock) or rejects (no-op).
    if (!window.currentUser) throw new Error('Not signed in.');

    const { error } = await sbClient.from('equipment_transfers').insert({
      equipment_id:   item.id,
      equipment_name: item.name,
      category:       item.category,
      from_site: from, to_site: to, quantity: qty,
      transferred_by: window.currentUser.id,
      foreman_name:   window.currentProfile?.full_name || '',
      notes,
      status:         'pending',
    });
    if (error) throw error;

    closeFTransfer();
    showFTransferToast('Transfer requested — waiting for admin approval.');
    // No need to reload — quantities haven't actually changed yet.

  } catch (e) {
    errEl.textContent = 'Could not submit request: ' + e.message;
  } finally {
    if (confirmBtn) { confirmBtn.disabled = false; confirmBtn.textContent = 'Confirm'; }
  }
}

// Lightweight toast for the foreman after a transfer is requested. We don't
// reuse the global toast here because the equipment overlay sits on top of it.
function showFTransferToast(msg) {
  let toast = document.getElementById('fTransferToast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'fTransferToast';
    toast.style.cssText = 'position:fixed;left:50%;bottom:32px;transform:translateX(-50%);background:#1a2e4a;color:#fff;padding:12px 20px;border-radius:8px;font-size:14px;font-weight:600;box-shadow:0 4px 16px rgba(0,0,0,.3);z-index:10002;opacity:0;transition:opacity .2s';
    document.body.appendChild(toast);
  }
  toast.textContent = msg;
  toast.style.opacity = '1';
  clearTimeout(toast._t);
  toast._t = setTimeout(() => { toast.style.opacity = '0'; }, 3200);
}

function escEq(str) {
  return String(str ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}
